import { useCallback, useState } from 'react';
import { useParams } from 'react-router-dom';
import { usePriceListFileMutation, useSelectedBrandsBySupplierIdQuery, useSupplierPriceListCreateMutation } from 'api';
import { Form, notification, Upload } from 'components';
import { dateFormatter, MAX_FILE_SIZE } from 'constants/index';
import { ErrorMappingValueType } from 'typings';

export const useAddEdit = ({ onCancel }: { onCancel: () => void }) => {
  const [isSaveBtnDisabled, setIsSaveBtnDisabled] = useState(true);
  const { id } = useParams();
  const [form] = Form.useForm();
  const { data = [] } = useSelectedBrandsBySupplierIdQuery({ supplierId: id });

  const [createPricelist, { isLoading: createIsLoading }] = useSupplierPriceListCreateMutation();
  const [createPricelistFile, { isLoading: createFileIsLoading }] = usePriceListFileMutation();

  const [notificationApi, contextHolder] = notification.useNotification();

  const openNotificationWithIcon = useCallback(
    (messageContent: ErrorMappingValueType) => {
      const { type, message } = messageContent;
      notificationApi[type]({
        message: message,
      });
    },
    [notificationApi]
  );

  const handleFileSizeValidation = (file: File) => {
    if (file.size > MAX_FILE_SIZE) {
      form.setFields([
        {
          name: 'priselist',
          errors: [`File exceeds the maximum size of 5 MB.`],
          value: [],
        },
      ]);
      setIsSaveBtnDisabled(true);
      return Upload.LIST_IGNORE; // Prevent adding the file
    } else {
      form.setFields([
        {
          name: 'priselist',
          errors: undefined,
          value: [file],
        },
      ]);
      setIsSaveBtnDisabled(false);
    }
    return false;
  };

  const handleErrors = useCallback(
    (error: any) => {
      if (!error?.data?.success && error?.data?.data) {
        Object.values(error.data.data).forEach((message) => {
          openNotificationWithIcon({
            type: 'error',
            message: message as string,
          });
        });
      } else {
        openNotificationWithIcon({
          type: 'error',
          message: 'Something went wrong!',
        });
      }
    },
    [openNotificationWithIcon]
  );

  const handleSubmit = useCallback(async () => {
    try {
      const { priselist, expiryDate, brandIds } = form.getFieldsValue();
      const payload = {
        supplierId: id,
        brandIds,
        expireDate: dateFormatter(expiryDate, 'end'),
      };
      const formData = new FormData();
      if (priselist?.length && id) {
        const file = priselist[0];
        formData.append('files', file.originFileObj, file.name);
      }
      const response = await createPricelist(payload);
      if (response?.data?.success) {
        if (formData.has('files')) {
          formData.append('priceListId', response.data.data);
          const fileResponse = await createPricelistFile(formData);
          if (fileResponse?.data?.success) {
            form.resetFields();
            openNotificationWithIcon({
              type: 'success',
              message: 'Pricelist was successfully created.',
            });
            onCancel();
          } else {
            handleErrors(fileResponse.error);
          }
        } else {
          openNotificationWithIcon({
            type: 'success',
            message: 'Pricelist was successfully created.',
          });
          onCancel();
        }
      } else {
        handleErrors(response.error);
      }
    } catch (err) {
      handleErrors(err);
    }
  }, [form, createPricelist, createPricelistFile, id, onCancel, openNotificationWithIcon, handleErrors]);

  return {
    contextHolder,
    form,
    handleSubmit,
    isLoading: createIsLoading || createFileIsLoading,
    handleFileSizeValidation,
    isSaveBtnDisabled,
    data,
  };
};
